import React, {useMemo, useState} from "react";
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {Clear} from "@mui/icons-material";
import {Dialog2} from "../Dialog2";
import {useUserPreferences} from "./UserPreferencesProvider";
import {describeUserPrefKey, findUserPrefDef, UserPrefDef} from "./keys";
import {useConfirm} from "../Confirm";

type Group = {
    def: UserPrefDef | null;
    items: { key: string; value: string }[];
}

export function UserPreferencesDialog(props: {
    onClose(): void;
}) {
    const ctx = useUserPreferences();
    const confirm = useConfirm();

    const groups = useMemo(() => {
        const list: Group[] = [];
        const byDef = new Map<UserPrefDef | null, Group>();

        Object.keys(ctx.prefs).sort().forEach(key => {
            const def = findUserPrefDef(key);
            let g = byDef.get(def);
            if (!g) {
                g = {def, items: []};
                byDef.set(def, g);
                list.push(g);
            }

            g.items.push({key, value: ctx.prefs[key]});
        });

        // known groups first, unknown keys last
        list.sort((a, b) => (a.def ? 0 : 1) - (b.def ? 0 : 1));
        return list;
    }, [ctx.prefs]);

    const keys = Object.keys(ctx.prefs);

    return (
        <Dialog2 open onClose={() => props.onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>Preferences</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary">
                    These are saved to your account and apply on every device.
                </Typography>
                <div style={{height: 12}} />

                {keys.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        You don't have any preferences saved yet.
                    </Typography>
                )}

                {groups.map((g, i) => (
                    <div key={g.def ? g.def.key : "other"} style={{marginTop: i === 0 ? 0 : 16}}>
                        <Typography variant="subtitle2">{g.def ? g.def.label : "Other"}</Typography>
                        {g.def && <Typography variant="caption" color="textSecondary">{g.def.description}</Typography>}
                        <div style={{height: 4}} />
                        {g.items.map(item => (
                            <PrefRow key={item.key} prefKey={item.key} value={item.value} def={g.def}
                                     onChange={v => ctx.set(item.key, v)}
                                     onClear={() => ctx.clear(item.key)} />
                        ))}
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                {keys.length > 0 && <Button color="error" onClick={async () => {
                    try {
                        await confirm.confirm("Clear all " + keys.length + " preference(s)?");
                    } catch (e: any) {
                        return;
                    }

                    keys.forEach(k => ctx.clear(k));
                }}>Clear all</Button>}
                <div style={{flex: 1}} />
                <Button onClick={() => props.onClose()}>Close</Button>
            </DialogActions>
        </Dialog2>
    );
}

function PrefRow(props: {
    prefKey: string;
    value: string;
    def: UserPrefDef | null;
    onChange(value: string): void;
    onClear(): void;
}) {
    const kind = props.def ? props.def.kind : "string";
    const [draft, setDraft] = useState(props.value);
    const editable = kind === "string" || kind === "number";

    const commit = () => {
        if (draft === props.value) return;
        if (kind === "number" && isNaN(parseFloat(draft))) {
            setDraft(props.value);
            return;
        }

        props.onChange(draft);
    };

    return (
        <div style={{display: "flex", alignItems: "center", gap: 8, padding: "4px 0"}}>
            <div style={{flex: 1, minWidth: 0}}>
                <Typography variant="body2" noWrap>{describeUserPrefKey(props.prefKey)}</Typography>
                {describeUserPrefKey(props.prefKey) !== props.prefKey && (
                    <Typography variant="caption" color="textSecondary" noWrap component="div">{props.prefKey}</Typography>
                )}
            </div>
            <div style={{width: 180}}>
                {editable ? (
                    <TextField size="small" fullWidth value={draft}
                               type={kind === "number" ? "number" : "text"}
                               onChange={e => setDraft(e.target.value)}
                               onBlur={commit}
                               onKeyDown={e => {
                                   if (e.key === "Enter") commit();
                               }}/>
                ) : (
                    <Typography variant="body2">{props.value}</Typography>
                )}
            </div>
            <Tooltip title="Clear">
                <IconButton size="small" onClick={() => props.onClear()}><Clear fontSize="small"/></IconButton>
            </Tooltip>
        </div>
    );
}
